import type { AppFlatSetting } from '@sancare/typescript-commons/src/settings/types'

import type { McoStaySuggestionItem, McoStaySuggestionWildcardChild } from '@/misc/types'
import { ModeEnum } from '@/store/modules/settings/types'

export type AggregatedDasSuggestion = {
  reference: string
  description: string | null
  level: number | null
  isChronic: boolean
  confidence: number | null
  // Other valid wildcard children (e.g. I48* -> I481, I482... besides the I480 shown above),
  // revealed when the user unfolds this suggestion. Empty for a non-wildcard suggestion.
  otherWildcardChildren: Omit<AggregatedDasSuggestion, 'otherWildcardChildren'>[]
}

type RealDiagnosisSource = Pick<McoStaySuggestionItem | McoStaySuggestionWildcardChild, 'reference' | 'isChronic' | 'chronicDescription' | 'chronicLevel' | 'searches' | 'predictions' | 'description' | 'level'>

function computeBestConfidence(predictions: RealDiagnosisSource['predictions']): number | null {
  return predictions.reduce<number | null>((max, prediction) => (
    prediction.confidence !== null && (max === null || prediction.confidence > max) ? prediction.confidence : max
  ), null)
}

// Highest level first (unknown level last), then alphabetical by reference.
function compareByLevelThenReference(a: Pick<AggregatedDasSuggestion, 'level' | 'reference'>, b: Pick<AggregatedDasSuggestion, 'level' | 'reference'>): number {
  return (b.level ?? -1) - (a.level ?? -1) || a.reference.localeCompare(b.reference)
}

function isRealSuggestionVisible(real: RealDiagnosisSource, bestConfidence: number | null, threshold: number, requiresRevaluating: boolean, ghmLevel: number | null): boolean {
  const isOverThreshold = bestConfidence !== null && bestConfidence >= threshold
  const level = real.level ?? real.chronicLevel
  const isRevaluating = !requiresRevaluating || (ghmLevel !== null && level !== null && level > ghmLevel)
  return real.isChronic || real.searches.length > 0 || (isOverThreshold && isRevaluating)
}

function buildRealSuggestion(real: RealDiagnosisSource, threshold: number, requiresRevaluating: boolean, ghmLevel: number | null) {
  const bestConfidence = computeBestConfidence(real.predictions)
  return {
    suggestion: {
      reference: real.reference,
      description: real.description ?? real.chronicDescription,
      level: real.level ?? real.chronicLevel,
      isChronic: real.isChronic,
      confidence: bestConfidence,
    },
    isVisible: isRealSuggestionVisible(real, bestConfidence, threshold, requiresRevaluating, ghmLevel),
  }
}

/**
 * Aggregates the DAS suggestions of every RUM into a single read-only list (no RUM selector here).
 * Mirrors DiagnosisListStandardMode.vue's buildSuggestion visibility check, minus its RUM filter and
 * the isCurrentDiag/isExcluded flags, which need `stay_full`-only data.
 *
 * The isRevaluating gate is kept: in revalo mode (computeSuggestionFlags' default branch), an
 * over-threshold DAS only counts if its level exceeds the stay's current GHM level — otherwise
 * coding it wouldn't change the GHM. Dialysis mode skips this gate (mirrors the real page's
 * `mode === DIALYSIS` branch, which shows every over-threshold DAS regardless of level).
 *
 * A wildcard (e.g. I48*) is kept as a single grouped suggestion: displayed as its first valid child
 * (BUILD-830 mockup, e.g. I480), with the other valid children available under
 * `otherWildcardChildren` for the aside to unfold on demand.
 *
 * Suggestions, and the valid children of a wildcard, are sorted by descending level then by reference.
 */
export function buildAggregatedDasSuggestions(
  items: McoStaySuggestionItem[],
  appSettings: AppFlatSetting,
  stayHasInitialCoding: boolean,
  mode: ModeEnum,
  ghmLevel: number | null
): AggregatedDasSuggestion[] {
  const threshold = Number(appSettings[stayHasInitialCoding ? 'standard_mode_das_threshold' : 'new_coding_das_threshold'])
  const requiresRevaluating = mode === ModeEnum.OPTIMIZABLE

  return items
    .filter((item) => item.type === 'DAS')
    .map((item): AggregatedDasSuggestion | null => {
      if (!item.isWildcard) {
        const { suggestion, isVisible } = buildRealSuggestion(item, threshold, requiresRevaluating, ghmLevel)
        return isVisible ? { ...suggestion, otherWildcardChildren: [] } : null
      }

      // A wildcard's own isChronic/searches/predictions are matched at the group level (e.g. a
      // saved search on "A06*" as a whole, not on any specific child) — when that's the case every
      // child is eligible, not just the ones individually matched.
      const isGroupVisible = isRealSuggestionVisible(item, computeBestConfidence(item.predictions), threshold, requiresRevaluating, ghmLevel)
      const validChildren = item.wildcardChildren
        .map((child) => buildRealSuggestion(child, threshold, requiresRevaluating, ghmLevel))
        .filter(({ isVisible }) => isVisible || isGroupVisible)
        .map(({ suggestion }) => suggestion)
        .sort(compareByLevelThenReference)
      const [representative, ...otherWildcardChildren] = validChildren

      return representative ? { ...representative, otherWildcardChildren } : null
    })
    .filter((suggestion): suggestion is AggregatedDasSuggestion => suggestion !== null)
    .sort(compareByLevelThenReference)
}
